import React, { FC, useCallback, useEffect, useState } from 'react';
import { InputBase } from '@material-ui/core';
import { Autocomplete, Skeleton } from '@material-ui/lab';
import { LegacyEmployee } from 'src/interfaces/api/Employee';
import { createUseStyles } from 'src/theming';
import useAuth from 'src/hooks/api/useAuth';

interface Props {
  error?: boolean;
  className?: any;
  onPickEmployee: (employeeId: LegacyEmployee['id']) => void;
}

const useStyles = createUseStyles({
  input: {
    fontSize: '0.8125rem !important',
    padding: '0 !important',
  },
});

const LegacyEmployeeInput: FC<Props> = React.forwardRef<any, Props>(({ onPickEmployee, className }, ref) => {
  const [employees, setEmployees] = useState<LegacyEmployee[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const { fetch } = useAuth();
  const classes = useStyles();

  const fetchEmployees = (): Promise<LegacyEmployee[]> => {
    const queryURL = '/api/employee/';
    const graphQL = '{ employees(all: true){ id, firstname, lastname } }';

    return fetch(queryURL + graphQL, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'GET',
    })
      .then((resp) => resp.json())
      .then((data) => {
        if (data['data']['employees']) {
          return data['data']['employees'];
        }

        throw new Error('Aucun employé trouvé ...');
      });
  };

  const getEmployee = useCallback((employeeId: number) => employees.find((employee) => employee.id === employeeId), [
    employees,
  ]);

  const getEmployeeLabel = useCallback(
    (employeeId: number) => {
      const employee = getEmployee(employeeId);
      return `${employee?.firstname} ${employee?.lastname}`;
    },
    [employees]
  );

  useEffect(() => {
    fetchEmployees()
      .then((employees) => {
        setEmployees(employees);
        setLoading(false);
      })
      .catch((e) => {
        setError(e.message);
        setLoading(false);
      });
  }, []);

  const handleChange = (event: any, value: number | null) => {
    if (value) {
      onPickEmployee(value);
    }
    setInputValue('');
  };

  const handleInputValueChange = (event: any) => {
    const newValue = event.target.value;
    setInputValue(newValue);
  };

  if (loading) {
    return <Skeleton height={50} />;
  }

  if (error) {
    return <div style={{ color: '#f44336' }}>{error}</div>;
  }

  return (
    <div className={className}>
      <Autocomplete
        ref={ref}
        className={className}
        onChange={handleChange}
        inputValue={inputValue}
        clearOnBlur
        value={null}
        options={employees.map((employee) => employee.id)}
        getOptionLabel={(employeeId) => getEmployeeLabel(employeeId)}
        renderInput={(params) => (
          <div ref={params.InputProps.ref}>
            <InputBase
              classes={{ input: classes.input }}
              {...params.inputProps}
              onChange={handleInputValueChange}
              onBlur={() => setInputValue('')}
              placeholder='Ajouter un employé ...'
              fullWidth
            />
          </div>
        )}
      />
    </div>
  );
});

export default React.memo(LegacyEmployeeInput);
